import { Pencil, Trash2, RotateCcw } from "lucide-react"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { txTypeColor } from "@/app/lib/txColors"
import { interestTypeLabel, interestTypeColor } from "@/app/lib/interestType"
import { EditedBadge } from "./EditedBadge"
import type { ClientTheme } from "@/app/lib/themes"
import type { InterestKind } from "@/app/pages/NewTransactionPage/types"
import type { Transaction } from "@/app/types/inventory"

export type { Transaction }

interface Props {
  tx: Transaction
  /** Active tab — decides the NRI/ORRI Res (leasehold) vs Royalty/Legal Desc
   *  (mineral) column pair. Must match the parent table's header. */
  kind: InterestKind
  index: number
  hoveredRow: number | null
  onMouseEnter: () => void
  onMouseLeave: () => void
  theme: ClientTheme
  canWrite: boolean
  onEdit: () => void
  onDelete: () => void
  onRestore: () => void
  confirmingDelete: boolean
  onConfirmDelete: () => void
  onCancelDelete: () => void
  deleting: boolean
  restoring: boolean
}

export function TransactionRow({
  tx,
  kind,
  index,
  hoveredRow,
  onMouseEnter,
  onMouseLeave,
  theme,
  canWrite,
  onEdit,
  onDelete,
  onRestore,
  confirmingDelete,
  onConfirmDelete,
  onCancelDelete,
  deleting,
  restoring,
}: Props) {
  const isAcq = tx.type === "ACQ"
  const netAcres = Number(tx.net_acres)
  const valueColor = txTypeColor(tx.type, netAcres)
  const accentColor = valueColor
  const interestAccent = interestTypeColor(tx.interest_type)
  const isDeleted = !!tx.deleted_at

  return (
    <tr
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        borderBottom: `1px solid ${theme.borderSubtle}`,
        backgroundColor: hoveredRow === index ? theme.bgSurface2 : theme.bgSurface,
        transition: "background-color 120ms ease",
        opacity: isDeleted ? 0.55 : 1,
        textDecoration: isDeleted ? "line-through" : undefined,
      }}
    >
      <td style={{ padding: 0, width: "1px", backgroundColor: accentColor }} />
      <td className="px-3.5 py-2">
        <span
          className="flex items-center gap-1.5"
          style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}
        >
          {tx.transaction_date}
          {tx.app_edited_at && <EditedBadge theme={theme} />}
        </span>
      </td>
      <td className="px-3.5 py-2">
        <span
          className="inline-block px-2 py-0.5 rounded-full"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "11px",
            fontWeight: 500,
            backgroundColor: `${valueColor}1A`,
            color: valueColor,
          }}
        >
          {isAcq ? "Acquisition" : tx.type === "DIV" ? "Divestiture" : tx.type}
        </span>
      </td>
      <td className="px-3.5 py-2">
        <span
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "12px",
            fontWeight: 500,
            color: theme.textPrimary,
          }}
        >
          {tx.str}
        </span>
      </td>
      <td className="px-3.5 py-2">
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
          {tx.township_range}
        </span>
      </td>
      {/* Shrink-to-content: values are only ever short codes (PT / HBP). */}
      <td className="px-3 py-3" style={{ width: "1%", whiteSpace: "nowrap" }}>
        {tx.interest_type ? (
          <span
            className="inline-block px-2 py-0.5 rounded-full"
            title={interestTypeLabel(tx.interest_type)}
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "11px",
              fontWeight: 500,
              color: interestAccent ?? theme.textMuted,
              backgroundColor: interestAccent ? `${interestAccent}1A` : theme.bgSurface2,
              border: `1px solid ${interestAccent ? `${interestAccent}33` : theme.borderColor}`,
            }}
          >
            {tx.interest_type}
          </span>
        ) : (
          <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}>
            —
          </span>
        )}
      </td>
      <td className="px-3.5 py-2 text-right">
        <span
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "12px",
            fontWeight: 500,
            color: valueColor,
          }}
        >
          {formatNumber(netAcres, 4)}
        </span>
      </td>
      {kind === "leasehold" ? (
        <>
          <td className="px-3.5 py-2 text-right">
            <span
              style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textPrimary }}
            >
              {formatNumber(tx.nri, 4)}
            </span>
          </td>
          <td className="px-3.5 py-2 text-right">
            {Number(tx.orri_reserved) > 0 ? (
              <span
                style={{
                  fontFamily: "var(--font-data)",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.textPrimary,
                }}
              >
                {decimalToPercent(tx.orri_reserved, 2)}
              </span>
            ) : (
              <span
                style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textDim }}
              >
                —
              </span>
            )}
          </td>
        </>
      ) : (
        <>
          {/* Royalty is a fraction at rest (1.00 = 100%) — display 4dp. */}
          <td className="px-3.5 py-2 text-right">
            {tx.royalty == null ? (
              <span
                style={{ fontFamily: "var(--font-data)", fontSize: "12px", color: theme.textDim }}
              >
                —
              </span>
            ) : (
              <span
                style={{
                  fontFamily: "var(--font-data)",
                  fontSize: "12px",
                  color: theme.textPrimary,
                }}
              >
                {formatNumber(tx.royalty, 4)}
              </span>
            )}
          </td>
          <td className="px-3.5 py-2">
            <span
              style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textPrimary }}
              className="truncate max-w-[12rem] block"
            >
              {tx.legal_desc ?? "—"}
            </span>
          </td>
        </>
      )}
      <td className="px-3.5 py-2">
        <span
          style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textPrimary }}
          className="truncate max-w-[10rem] block"
        >
          {tx.counterparty ?? "—"}
        </span>
      </td>
      <td className="px-3.5 py-2">
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
          {tx.book_page ?? "—"}
        </span>
      </td>
      {canWrite && (
        <td className="px-3.5 py-2">
          <div className="flex items-center justify-end gap-2">
            {isDeleted ? (
              <button
                type="button"
                onClick={onRestore}
                disabled={restoring}
                className="btn"
                style={iconButton(theme)}
                title="Restore"
                aria-label="Restore transaction"
              >
                <RotateCcw size={13} />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  className="btn"
                  style={iconButton(theme)}
                  title="Edit"
                  aria-label="Edit transaction"
                >
                  <Pencil size={13} />
                </button>
                {confirmingDelete ? (
                  <span className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={onConfirmDelete}
                      disabled={deleting}
                      className="btn"
                      style={{ ...iconButton(theme), color: theme.brandAccent }}
                      title="Confirm delete"
                      aria-label="Confirm delete"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={onCancelDelete}
                      className="btn"
                      style={iconButton(theme)}
                      title="Cancel"
                      aria-label="Cancel delete"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={onDelete}
                    className="btn"
                    style={{ ...iconButton(theme), color: theme.brandAccent }}
                    title="Delete"
                    aria-label="Delete transaction"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  )
}

function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
